'use client';

import clsx from 'clsx';
import { m } from 'motion/react';
import Link from 'next/link';

import { useModal } from '@/app/pageContext';

import { CaseFile } from './CaseFile';
import { Button, Frame } from './primitives';

export function Hero({
  title,
  lede,
  cta,
  payer,
  statesCount,
  breadcrumb,
}: {
  title: string;
  lede: string;
  cta: string;
  payer: string;
  statesCount: number;
  breadcrumb?: { label: string };
}) {
  const { openModal } = useModal();
  const longTitle = title.length > 38;

  const rise = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const } },
  };

  return (
    <div className="pt-2.5 sm:pt-4">
      <Frame as="section" className="on-ink relative bg-ink-950 text-white" aria-labelledby="hero-heading">
        <div aria-hidden="true" className="beacon-glow absolute inset-0" />
        <div className="relative grid grid-cols-1 gap-12 px-6 pt-28 pb-10 sm:px-10 sm:pt-36 sm:pb-14 lg:grid-cols-12 lg:items-center lg:gap-8 lg:px-16 lg:pt-44 lg:pb-24">
          <div className="text-center lg:col-span-7 lg:text-left">
            {breadcrumb ? (
              <nav aria-label="Breadcrumb" className="mb-6 text-sm text-mist">
                <ol className="flex items-center justify-center gap-2 lg:justify-start">
                  <li>
                    <Link href="/" className="rounded-sm underline decoration-white/30 underline-offset-4 hover:text-white">
                      Home
                    </Link>
                  </li>
                  <li aria-hidden="true">/</li>
                  <li aria-current="page" className="text-white/90">
                    {breadcrumb.label}
                  </li>
                </ol>
              </nav>
            ) : null}
            {/* The headline is static so it paints before any script runs. */}
            <h1
              id="hero-heading"
              className={clsx(
                'font-display mx-auto text-balance leading-[0.96] font-extrabold tracking-[-0.035em] text-white lg:mx-0',
                longTitle
                  ? 'max-w-[16ch] text-[clamp(2.25rem,5.4vw,4.75rem)]'
                  : 'max-w-[12ch] text-[clamp(2.75rem,6.4vw,5.75rem)]',
              )}
            >
              {title}
            </h1>

            <m.div
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } } }}
            >
              <m.p
                variants={rise}
                className="mx-auto mt-7 max-w-[32rem] text-pretty text-lg leading-relaxed text-mist lg:mx-0 sm:text-xl"
              >
                {lede}
              </m.p>

              <m.div
                variants={rise}
                className="mt-9 flex flex-wrap items-center justify-center gap-x-5 gap-y-4 lg:justify-start"
              >
                <Button variant="beacon" className="px-7" onClick={openModal}>
                  {cta}
                </Button>
                <a
                  href="#process"
                  className="inline-flex min-h-12 items-center rounded-full border border-white/25 px-6 text-[0.95rem] font-semibold text-white transition-colors hover:border-white hover:bg-white/[0.06]"
                >
                  See how it works
                </a>
              </m.div>

              <m.p variants={rise} className="mt-8 text-sm text-mist">
                Built with US sleep programs. Payer guides for {statesCount} states. HIPAA-ready.
              </m.p>
            </m.div>
          </div>

          <div className="flex justify-center lg:col-span-5 lg:justify-end">
            <CaseFile payer={payer} delay={0.4} />
          </div>
        </div>
      </Frame>
    </div>
  );
}
